/*  ========================================================================
    # Sections - Projects
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Project from '../../components/Project';
import useProjectsApi from '../../hooks/useProjectsApi';
import useTranslation from '../../hooks/useTranslation';
import useLocalization from '../../hooks/useLocalization';
import useHistoryValues from '../../hooks/useHistoryValues';
import useGetParams from '../../hooks/useGetParams';
import bem from '../../helpers/bem';
import AnimateHeight from 'react-animate-height';
import useBreakpoints from '../../hooks/useBreakpoints';
import tailwindConfig from '../../../../tailwind.config';
import NoEntries from '../../components/NoEntries';

const getCategoryIdBySlug = (slug, categories) => {
  const category = categories.find(cat => cat.slug === slug);
  if (category) return category.cat_ID;
  return '';
};

const getCategorySlugById = (id, categories) => {
  const category = categories.find(cat => cat.cat_ID === parseInt(id, 10));
  if (category) return category.slug;
  return undefined;
};

const getCategoryIdsFromParams = (defaults, categories) => {
  let defaultCats = []; // eslint-disable-line
  if (defaults) {
    const defaultsArray = defaults.split(',');
    defaultsArray.forEach(element => {
      defaultCats.push(getCategoryIdBySlug(element, categories));
    });
  }
  return defaultCats;
};

const Projects = ({ heading, categories, maxCount }) => {
  const __ = useTranslation();
  const parameterCategory = __('param-category');
  const getParams = useGetParams();

  const { xlUp } = useBreakpoints();

  // Get pre selected categories by url parameter
  const defaultCategories = getCategoryIdsFromParams(getParams[parameterCategory], categories);
  const [storedProjects, setStoredProjects] = useState([]);
  const [appendedProjects, setAppendedProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(defaultCategories);
  const [projectsOffset, setProjectsOffset] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filterHeight, setFilterHeight] = useState(0);

  // Icons
  const local = useLocalization();
  const iconSearch = `${local('svgSprite')}#search`;

  // Ref
  const searchRef = useRef(null);

  const pushNewValues = useHistoryValues({
    [parameterCategory]: selectedCategories
  });

  const handleCategoryChange = catID => {
    let newCatsSlug = []; //eslint-disable-line
    if (selectedCategories.find(category => category === catID)) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories([catID]);
      // Push new Cats with slug to history params
      newCatsSlug.push(getCategorySlugById(catID, categories));
    }

    setProjectsOffset(0);
    setStoredProjects([]);
    setAppendedProjects([]);

    pushNewValues({
      [parameterCategory]: newCatsSlug
    });
  };

  const handleSearchQueryChange = event => {
    event.preventDefault();
    // reset stored projects and offset
    if (searchRef?.current?.value !== '') {
      setProjectsOffset(0);
      setStoredProjects([]);
      setAppendedProjects([]);
      setSearchQuery(searchRef?.current?.value);
    }
  };

  const { projects, loadingProjects, projectsCount } = useProjectsApi({
    per_page: maxCount,
    offset: projectsOffset,
    search: searchQuery,
    categories: selectedCategories,
    order: 'desc',
    orderby: 'date'
  });

  // Merge results into one unified array
  const mergedProjects = [...storedProjects, ...appendedProjects];

  // Handle load more
  const loadMoreProjects = () => {
    if (projectsCount > maxCount && mergedProjects.length !== projectsCount) {
      setProjectsOffset(mergedProjects.length);
      setStoredProjects([...storedProjects, ...appendedProjects]);
    }
  };

  // useEffect for projects
  useEffect(() => {
    if (storedProjects.length < 1) {
      setStoredProjects(projects);
    } else {
      setAppendedProjects(projects);
    }
  }, [projects]);

  // general useEffect
  useEffect(() => {
    // Handler updates show filter for window sizes.
    const handler = () => {
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.xl, 10)) {
        setShowFilter(true);
      }
    };

    // Add event listener.
    window.addEventListener('resize', handler, { passive: true });

    // Call it initially.
    handler();

    // On component unmount, remove event listener.
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (showFilter) setFilterHeight('auto');
    else setFilterHeight(0);
  }, [showFilter]);

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  return (
    <div className={bem('s-projects', 'wrapper')}>
      <div className='container'>
        {heading && (
          <div className={bem('s-projects', 'header')}>
            <h2>{heading}</h2>
            <button
              title='Filter'
              onClick={toggleFilter}
              className={
                bem('s-projects', 'filter-button') +
                ` ` +
                bem('c-button', '', { 'outline-black': !showFilter, black: showFilter })
              }
              dangerouslySetInnerHTML={{ __html: 'Filter' }}
            ></button>
          </div>
        )}
        <AnimateHeight height={filterHeight} duration={250} delay={0}>
          <div className={bem('s-projects', 'filter-wrapper')}>
            <div className={bem('s-projects', 'filter')}>
              {categories.map(cat => (
                <button
                  title={cat.name}
                  className={bem('c-button', '', {
                    black: selectedCategories.find(el => el === cat.cat_ID),
                    'outline-black': !selectedCategories.find(el => el === cat.cat_ID)
                  })}
                  key={cat.cat_ID}
                  onClick={() => handleCategoryChange(cat.cat_ID)}
                  dangerouslySetInnerHTML={{ __html: cat.name }}
                />
              ))}
            </div>
            <form
              onSubmit={event => handleSearchQueryChange(event)}
              className={bem('s-projects', 'search')}
            >
              <input type='text' placeholder={__('projects-search-placeholder')} ref={searchRef} />
              <button onClick={event => handleSearchQueryChange(event)} title='button-search'>
                <svg viewBox='0 0 200 200'>
                  <use href={iconSearch} />
                </svg>
              </button>
            </form>
          </div>
        </AnimateHeight>
      </div>

      <div className={bem('u-background', 'divider', { top: true })} />
      <div className='u-background'>
        <div className='container'>
          {mergedProjects.length > 0 && (
            <>
              {mergedProjects.map((project, index) => (
                <Project
                  key={index}
                  image={project.image}
                  projectName={project.projectName}
                  customer={project.customer}
                  permalink={project.url}
                  description={project.excerpt}
                />
              ))}
            </>
          )}

          {loadingProjects && (
            <>
              {[...Array(3)].map((value, index) => (
                <Project key={index} skeleton={true} />
              ))}
            </>
          )}

          {mergedProjects.length === 0 && !loadingProjects && (
            <NoEntries text={__('section-projects-no-entries-text')} />
          )}

          {projectsCount > mergedProjects.length && (
            <div className='tw-flex tw-mt-6 tw-justify-center'>
              <button
                type='button'
                className={bem('c-button', '', {
                  black: true
                })}
                onClick={loadMoreProjects}
              >
                {__('projects-load-more')}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={bem('u-background', 'divider', { bottom: true })} />
    </div>
  );
};

Projects.propTypes = {
  heading: PropTypes.string,
  categories: PropTypes.array,
  maxCount: PropTypes.number
};

export default Projects;
